import { ReactNode } from 'react'
import { CircleHelp, UsersIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Select from 'app/frontend/base/components/Select'
import { RaidPlanAssignmentDependency } from 'app/frontend/types/custom/RaidPlan'
import { cn } from '@/lib/utils'
import SpellIcon from './SpellIcon'
import AssignmentTypeIcon from './AssignmentTypeIcon'
import { CUSTOM_ABILITY_SPELL_ID } from '../utils/encounterAssignmentRows'
import {
  AssignmentDependencyOption,
  decodeDependencyValue,
  encodeDependencyValue,
  findDependencyOption,
} from './assignmentDependencies'

const formatDependencyCount = ({
  min,
  max,
  disabling,
}: AssignmentDependencyOption['count']) => {
  if (disabling) return '?'
  return min === max ? `${min}` : `${min}-${max}`
}

export const AssignmentDependencyOptionLabel = ({
  option,
  showCount = true,
}: {
  option: AssignmentDependencyOption
  showCount?: boolean
}) => (
  <span className='flex min-w-0 items-center gap-2'>
    <SpellIcon
      spellId={option.isCustom ? CUSTOM_ABILITY_SPELL_ID : option.spellId}
      className='w-5 h-5 rounded border border-border shrink-0'
    />
    <AssignmentTypeIcon assignmentType={option.assignmentType} />
    <span className='truncate' style={{ color: option.color }}>
      {option.name}
    </span>
    {option.groupLabel ? (
      <span className='shrink-0 text-muted-foreground'>
        {option.groupLabel}
      </span>
    ) : null}
    {showCount ? (
      <span className='ml-auto inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground'>
        <UsersIcon className='h-3 w-3' />
        {formatDependencyCount(option.count)}
      </span>
    ) : null}
  </span>
)

type Props = {
  value?: RaidPlanAssignmentDependency
  options: AssignmentDependencyOption[]
  allOptions: AssignmentDependencyOption[]
  onChange: (next: RaidPlanAssignmentDependency | undefined) => void
  disabled?: boolean
  inheritedValue?: RaidPlanAssignmentDependency
  className?: string
}

const AssignmentDependencySelect = ({
  value,
  options,
  allOptions,
  onChange,
  disabled,
  inheritedValue,
  className,
}: Props) => {
  const { t } = useTranslation()
  const selectedOption = findDependencyOption(allOptions, value)
  const inheritedOption = findDependencyOption(allOptions, inheritedValue)
  const selectOptions = [
    ...options,
    ...(selectedOption && !options.includes(selectedOption)
      ? [selectedOption]
      : []),
  ].map((option, index) => ({
    value: `${option.abilityKey}:${option.groupIndex}`,
    sortLabel: index.toString().padStart(4, '0'),
    label: <AssignmentDependencyOptionLabel option={option} />,
    triggerLabel: (
      <AssignmentDependencyOptionLabel option={option} showCount={false} />
    ),
  }))
  const placeholder: ReactNode = inheritedOption ? (
    <span className='flex min-w-0 items-center gap-1 opacity-70'>
      <span className='shrink-0'>
        {t('dashboard.team.plans.assignments.depends-on-inherited')}
      </span>
      <AssignmentDependencyOptionLabel
        option={inheritedOption}
        showCount={false}
      />
    </span>
  ) : (
    t('dashboard.team.plans.assignments.depends-on-none')
  )

  return (
    <div className={cn('min-w-56', className)}>
      <div className='flex h-5 items-center gap-1'>
        <Label>{t('dashboard.team.plans.assignments.depends-on')}</Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex items-center text-muted-foreground'>
              <CircleHelp className='h-3.5 w-3.5' />
            </span>
          </TooltipTrigger>
          <TooltipContent className='max-w-72'>
            {t('dashboard.team.plans.assignments.depends-on-tooltip')}
          </TooltipContent>
        </Tooltip>
      </div>
      <Select
        value={selectedOption ? encodeDependencyValue(value) : undefined}
        setValue={(next) => onChange(decodeDependencyValue(next))}
        options={selectOptions}
        placeholder={placeholder}
        emptyMessage={t('dashboard.team.plans.assignments.depends-on-empty')}
        inModal
        isClearable
        className='h-8'
        disabled={disabled}
      />
    </div>
  )
}

export default AssignmentDependencySelect
